import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { RouterLink } from 'src/components/router-link';
import { Button, Card, CardContent, List } from '@mui/material';
import { ListItem, ListItemButton, ListItemText, Divider } from '@mui/material';
import { ListItemIcon, IconButton, SvgIcon } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import ReadOnlyTextField from 'src/components/widgets/readonly-textfield';
import XCircleIcon from 'src/icons/untitled-ui/free/x-circle';

export const DetailView = forwardRef((props, ref) => {
  const { item, handleAccountClick, handleAssignClick, handleRemoveClick, usePhoneView, usePers } = props;
  const [accountItems, setAccountItems] = useState([]);
  const { itemClick = false, assign = false, unassign = false } = usePers;

  useEffect(() => {
    setAccountItems(item.accounts || []);
  }, [item.accounts]);

  const [marked, setMarked] = useState(null);
  const handleMarkToggle = (account) => {
    const isAlreadyMarked = marked?.id === account.id;
    if (isAlreadyMarked) {
      setMarked(null);
    } else {
      setMarked(account);
    }
  };

  useImperativeHandle(ref, () => ({
    removeItem: (delItem) => {
      setMarked(null);
      
      if (!delItem || !delItem.studentId) return;
      setAccountItems((prev) => prev.filter((acc) => acc.id !== delItem.studentId));
    },
}));

  return (
    <Card >
      <CardContent sx={{ px: 1, py: 2 }}>
        <Grid container spacing={3}>
          <Grid xs={12} sm={4} md={3}>
            <><Grid container spacing={3}>
              <Grid xs={usePhoneView ? 6 : 12} md={12}>
                <ReadOnlyTextField label="Tên khóa" value={item.name} />
              </Grid>

              <Grid xs={usePhoneView ? 6 : 12} md={12}>
                <ReadOnlyTextField label="Mã khóa" value={item.code} enumType="code" />
              </Grid>

              <Grid xs={usePhoneView ? 6 : 12} md={12}>
                <ReadOnlyTextField label="Khai giảng" value={item.openingDay} dateFormat="dd/MM/yyyy" />
              </Grid>

              <Grid xs={usePhoneView ? 6 : 12} md={12}>
                <ReadOnlyTextField label="Bế giảng" value={item.closingDay} dateFormat="dd/MM/yyyy" />
              </Grid>

              {!usePhoneView && (
              <Grid xs={12} md={12}>
                <ReadOnlyTextField label="Ngày hết hạn" value={item.expireDay} dateFormat="dd/MM/yyyy" />
              </Grid>
              )}

              {assign && (
                <Grid xs={12} md={12} display="flex" 
                  sx={{ justifyContent: { xs: 'center', md: 'flex-end' } }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    onClick={() => {
                      if(marked)
                        handleRemoveClick(marked);
                      else
                        handleAssignClick(item);
                    }}
                    sx={{
                      bgcolor: marked ? 'error.main' : 'primary.main',
                      '&:hover': {
                        bgcolor: marked ? 'error.dark' : 'primary.dark',
                      },
                    }}
                  >
                    {marked ? 'Xoá học viên' : 'Thêm học viên'}
                  </Button>
                </Grid>
              )}
            </Grid>
            </>
          </Grid>
          <Grid xs={12} sm={8} md={9}>
            <List
              sx={{
                // height: isLandscape ? '100%' : 'calc(100vh - 240px)',
                height: '75vh',
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0,
                scrollbarWidth: 'thin',
              }}
            >
            {accountItems?.map((account, idx) => (
              <React.Fragment key={account.id || idx}>
                <ListItem disablePadding>
                  <ListItemButton
                      sx={{ py: 1, px: 1 }}
                      onClick={itemClick ? () => handleAccountClick(account) : undefined}
                    >
                    <ListItemText
                      primary={idx + 1}
                      primaryTypographyProps={{
                        fontWeight: 'normal',
                        fontSize: '0.875rem'
                      }}
                      sx={{ flex: '0 0 32px' }}
                    />
                    <ListItemText
                      sx={{ flex: 1.5 }}
                      disableTypography
                    >
                      {usePhoneView ? (
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{account.name}</div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 400 }}>{account.phone}</div>
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{account.name}</div>
                      )}
                    </ListItemText>

                    {!usePhoneView && (
                      <ListItemText
                        primary={account.phone}
                        primaryTypographyProps={{
                          variant: 'body2',
                        }}
                        sx={{
                          flex: 1,
                          textAlign: 'left',
                        }}
                      />
                    )}
                    {!usePhoneView && (
                      <ListItemText
                        primary={account.birthday}
                        primaryTypographyProps={{
                          variant: 'body2', 
                          textAlign: 'right'
                        }}
                        sx={{ flex: '0 0 95px', textAlign: 'right' }}
                      />
                    )}
                    {unassign && (
                      <ListItemIcon
                        sx={{
                          flex: '0 0 48px',
                          minWidth: 0,
                          justifyContent: 'right',
                          p: 0,
                          m: 0,
                          '& .MuiCheckbox-root': {
                            p: 0,
                            m: 0,
                          },
                        }}
                      >
                        <IconButton
                          component={RouterLink}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkToggle(account);
                          }}
                        >
                          <SvgIcon
                            sx={{
                              color: marked?.id === account.id ? 'error.main' : 'text.secondary',
                            }}
                          >
                            <XCircleIcon />
                          </SvgIcon>
                        </IconButton>
                      </ListItemIcon>
                    )}
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
            </List>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});

DetailView.propTypes = {
  item: PropTypes.object.isRequired,
};
